import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaHeadset,
  FaSync,
  FaUserCheck,
  FaDoorOpen,
  FaStickyNote,
  FaPaperPlane,
  FaCrown,
  FaCopy,
  FaRedo,
  FaExternalLinkAlt,
  FaTrash,
  FaBan,
  FaPlay,
  FaCalendarPlus,
  FaUserEdit,
  FaLink,
  FaHistory,
  FaBolt,
  FaImage,
  FaChartBar,
  FaCog,
  FaStar,
  FaClock,
  FaHourglassHalf,
  FaComments,
  FaRegCalendarAlt,
  FaLock,
} from 'react-icons/fa';
import {
  listSupportChats,
  syncSupportChat,
  sendSupportMessage,
  supportChatAction,
  deleteSupportChats,
  supportChatContext,
  supportChatVipAction,
  pingSupportTyping,
  fetchSupportSettings,
  updateSupportSettings,
  fetchSupportStats,
  SUPPORT_TOPICS,
} from '../../services/supportChatService';
import adminService from '../../services/adminService';
import useSupportSync from '../../hooks/useSupportSync';
import ConfirmDialog from './ConfirmDialog';
import { playNotifyBeep, unlockNotifyBeep } from '../../utils/notifyBeep';
import { discordAvatarUrl } from '../../utils/discordAvatar';
import { compressImageFile } from '../../utils/compressImage';
import SupportAttachmentImage from '../SupportChat/SupportAttachmentImage';
import './SupportDeskPanel.css';

function messageAvatarUrl(message, chat) {
  if (!message || message.authorType === 'system') return null;
  if (message.authorType === 'user') {
    return discordAvatarUrl(
      message.authorId || chat?.userId,
      message.authorAvatar ?? chat?.avatar,
      64
    );
  }
  return discordAvatarUrl(message.authorId, message.authorAvatar, 64);
}

const PLAN_OPTIONS = [
  { id: 'origin', label: 'Origin' },
  { id: 'eclipse', label: 'Eclipse' },
  { id: 'investidor', label: 'Investidor' },
];

const QUICK_REPLIES = [
  {
    id: 'wait',
    label: 'Aguarde',
    text: 'Recebemos sua mensagem. Já estamos verificando e já te retorno.',
  },
  {
    id: 'pix',
    label: 'PIX',
    text: 'Pode enviar o ID do pagamento PIX ou o comprovante (horário e valor) para confirmarmos?',
  },
  {
    id: 'reapplied',
    label: 'Reaplicado',
    text: 'Reaplicamos seu VIP (cargo Discord + rank no servidor). Entre novamente no jogo e no Discord para atualizar.',
  },
  {
    id: 'portal',
    label: 'Portal',
    text: 'Para gerenciar cartão/fatura, abra Minha Conta (/conta) e use o Portal Stripe.',
  },
  {
    id: 'nick',
    label: 'Nick',
    text: 'Confirme o nick Minecraft exatamente como está na conta VIP para alinharmos o rank.',
  },
];

const FILTERS = [
  { id: 'open', label: 'Abertos' },
  { id: 'queued', label: 'Fila' },
  { id: 'mine', label: 'Meus' },
  { id: 'active', label: 'Ativos' },
  { id: 'closed', label: 'Fechados' },
];

const WEEKDAY_ROWS = [
  { day: 1, label: 'Segunda' },
  { day: 2, label: 'Terça' },
  { day: 3, label: 'Quarta' },
  { day: 4, label: 'Quinta' },
  { day: 5, label: 'Sexta' },
  { day: 6, label: 'Sábado' },
  { day: 0, label: 'Domingo' },
];

const TIMEZONE_OPTIONS = [
  { id: 'America/Sao_Paulo', label: 'Brasília (UTC−3)' },
  { id: 'America/Manaus', label: 'Manaus (UTC−4)' },
  { id: 'America/Belem', label: 'Belém (UTC−3)' },
  { id: 'America/Fortaleza', label: 'Fortaleza (UTC−3)' },
  { id: 'America/Recife', label: 'Recife (UTC−3)' },
  { id: 'UTC', label: 'UTC' },
];

function scheduleDay(schedule, day) {
  if (!schedule || typeof schedule !== 'object') return null;
  const raw = schedule[day] !== undefined ? schedule[day] : schedule[String(day)];
  return raw && typeof raw === 'object' ? raw : null;
}

function toMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Formata duração (ex.: 45s, 3m 12s, 1h 05m). */
function formatElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const s = totalSec % 60;
  const totalMin = Math.floor(totalSec / 60);
  const m = totalMin % 60;
  const h = Math.floor(totalMin / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (totalMin > 0) return `${totalMin}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * Tempo relevante do chat para o staff:
 * - na fila: desde createdAt
 * - em atendimento: desde claimedAt (fallback createdAt)
 * - fechado: duração até closedAt
 */
function chatTimerInfo(chat, nowMs, slaQueueMinutes = 10) {
  if (!chat) return null;
  const created = toMs(chat.createdAt);
  const claimed = toMs(chat.claimedAt) || (chat.assigneeId ? created : null);
  const closed = toMs(chat.closedAt);
  const slaMs = Math.max(1, Number(slaQueueMinutes) || 10) * 60 * 1000;

  if (chat.status === 'closed') {
    const start = claimed || created;
    const end = closed || toMs(chat.updatedAt) || nowMs;
    if (!start) return null;
    return {
      kind: 'closed',
      label: `Durou ${formatElapsed(end - start)}`,
      breach: false,
    };
  }

  if (chat.assigneeId && claimed) {
    return {
      kind: 'active',
      label: `Em atendimento ${formatElapsed(nowMs - claimed)}`,
      breach: false,
    };
  }

  if (created) {
    const waited = nowMs - created;
    return {
      kind: 'queued',
      label: `Na fila ${formatElapsed(waited)}`,
      breach: waited >= slaMs,
    };
  }

  return null;
}

function formatMinutes(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value < 1) return `${Math.round(value * 60)}s`;
  return `${value.toFixed(1)} min`;
}

function formatReportDay(isoDate) {
  if (!isoDate || isoDate === '—') return isoDate || '—';
  try {
    const [y, m, d] = String(isoDate).split('-').map(Number);
    if (!y || !m || !d) return isoDate;
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return isoDate;
  }
}

function mergeMessages(prev, incoming) {
  if (!incoming?.length) return prev;
  const map = new Map(prev.map((m) => [m.seq, m]));
  for (const m of incoming) map.set(m.seq, m);
  return Array.from(map.values()).sort((a, b) => a.seq - b.seq);
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function PermBtn({
  allowed,
  lockedHint = 'Sem permissão',
  className = '',
  disabled = false,
  onClick,
  children,
  type = 'button',
}) {
  if (!allowed) {
    return (
      <button
        type={type}
        className={`${className} is-perm-locked`.trim()}
        disabled
        title={lockedHint}
        aria-label={lockedHint}
      >
        <FaLock aria-hidden="true" /> {children}
      </button>
    );
  }
  return (
    <button
      type={type}
      className={className}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const DISCORD_LOG_EVENT_OPTIONS = [
  { id: 'queued', label: 'Na fila' },
  { id: 'claim', label: 'Assumiu' },
  { id: 'release', label: 'Liberou' },
  { id: 'transfer', label: 'Transferiu' },
  { id: 'close', label: 'Fechou' },
  { id: 'reopen', label: 'Reabriu' },
];

const SupportDeskPanel = ({
  canEdit: canEditProp = true,
  canDelete: canDeleteProp = false,
  canChat: canChatProp = true,
  canClaim: canClaimProp = true,
  canRelease: canReleaseProp = true,
  canClose: canCloseProp = true,
  canTransfer: canTransferProp = true,
  canReport: canReportProp = true,
  canVipEdit: canVipEditProp = false,
  guildMembers = [],
  guildMembersLoading = false,
  onReloadMembers,
  guildChannels = [],
  guildChannelsLoading = false,
  guildRoles = [],
  onReloadChannels,
  onOpenVip,
  onOpenProfiles,
  onAuthError,
  focusChatId = null,
  onFocusChatConsumed,
}) => {
  const [filter, setFilter] = useState('open');
  const [query, setQuery] = useState('');
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({ queued: 0, mine: 0, open: 0 });
  const [serverCaps, setServerCaps] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  // Fonte da verdade: capabilities da API (sessão do requireAdmin a cada listagem)
  const canEdit = serverCaps ? Boolean(serverCaps.canEdit) : canEditProp;
  const canDelete = serverCaps ? Boolean(serverCaps.canDelete) : canDeleteProp;
  const canChat = serverCaps ? Boolean(serverCaps.canChat) : canChatProp;
  const canClaim = serverCaps ? Boolean(serverCaps.canClaim) : canClaimProp;
  const canRelease = serverCaps
    ? Boolean(serverCaps.canRelease)
    : canReleaseProp;
  const canClose = serverCaps ? Boolean(serverCaps.canClose) : canCloseProp;
  const canTransfer = serverCaps
    ? Boolean(serverCaps.canTransfer)
    : canTransferProp;
  const canReport = serverCaps ? Boolean(serverCaps.canReport) : canReportProp;
  const canVipEdit = serverCaps
    ? Boolean(serverCaps.canVipGrant) || canVipEditProp
    : canVipEditProp;
  const [selectedId, setSelectedId] = useState(null);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [context, setContext] = useState(null);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [noteMode, setNoteMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [transferId, setTransferId] = useState('');
  const [resolution, setResolution] = useState('Problema resolvido');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmVip, setConfirmVip] = useState(null);
  const [editNick, setEditNick] = useState('');
  const [editPlan, setEditPlan] = useState('origin');
  const [grantNick, setGrantNick] = useState('');
  const [grantPlan, setGrantPlan] = useState('origin');
  const listRef = useRef(null);
  const typingTimer = useRef(null);
  const queryRef = useRef('');
  const resetRef = useRef(() => {});
  const unreadMapRef = useRef(new Map());
  const listReadyRef = useRef(false);
  const selectedIdRef = useRef(null);
  const fileRef = useRef(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [pendingImage, setPendingImage] = useState(null);
  const [slaQueueMinutes, setSlaQueueMinutes] = useState(10);
  const [showStats, setShowStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [channelOptions, setChannelOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [channelsLoadingLocal, setChannelsLoadingLocal] = useState(false);
  const [channelsError, setChannelsError] = useState('');

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    fetchSupportSettings()
      .then((data) => {
        if (data.settings) {
          setSlaQueueMinutes(data.settings.slaQueueMinutes || 10);
          setSettingsDraft(data.settings);
        }
      })
      .catch(() => {});
  }, []);

  const selectedTimer = useMemo(
    () => chatTimerInfo(chat, nowMs, slaQueueMinutes),
    [chat, nowMs, slaQueueMinutes]
  );

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await fetchSupportStats({ days: 14 });
      setStats(data);
    } catch (err) {
      setError(err.message || 'Falha ao carregar relatório');
      onAuthError?.(err);
    } finally {
      setStatsLoading(false);
    }
  }, [onAuthError]);

  const loadChannels = useCallback(async () => {
    setChannelsLoadingLocal(true);
    setChannelsError('');
    try {
      const resources = await adminService.fetchGuildResources();
      const list = Array.isArray(resources?.channels) ? resources.channels : [];
      const roles = Array.isArray(resources?.roles) ? resources.roles : [];
      setChannelOptions(list);
      setRoleOptions(roles.filter((r) => r && !r.managed));
      if (!list.length) {
        setChannelsError('Nenhum canal de texto encontrado neste servidor.');
      }
    } catch (err) {
      setChannelOptions([]);
      setRoleOptions([]);
      setChannelsError(
        err?.message || 'Falha ao carregar canais do Discord'
      );
      onAuthError?.(err);
    } finally {
      setChannelsLoadingLocal(false);
    }
  }, [onAuthError]);

  const logChannelList = channelOptions.length
    ? channelOptions
    : guildChannels;
  const logRoleList = roleOptions.length
    ? roleOptions
    : (guildRoles || []).filter((r) => r && !r.managed);
  const logChannelsBusy = channelsLoadingLocal || guildChannelsLoading;

  useEffect(() => {
    if (showStats) loadStats();
  }, [showStats, loadStats]);

  useEffect(() => {
    // Só força nota interna quando há Gerenciar sem Chat
    if (!canChat && canEdit) setNoteMode(true);
    // Com Chat e sem Gerenciar: sempre modo conversa com o jogador
    else if (canChat && !canEdit) setNoteMode(false);
  }, [canChat, canEdit]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await listSupportChats({
        filter,
        q: queryRef.current,
        limit: 60,
      });
      const chats = data.chats || [];

      // Blim só em chats que eu assumi (e não no chat aberto agora)
      const myId = String(adminService.getAdminUser()?.id || '');
      if (listReadyRef.current && myId) {
        let shouldBeep = false;
        for (const c of chats) {
          if (String(c.assigneeId || '') !== myId) continue;
          if (c.id === selectedIdRef.current) continue;
          const prev = unreadMapRef.current.get(c.id);
          if (prev === undefined) continue;
          const next = Number(c.unreadStaff) || 0;
          if (next > prev) {
            shouldBeep = true;
            break;
          }
        }
        if (shouldBeep) playNotifyBeep();
      }

      const nextMap = new Map();
      for (const c of chats) {
        if (myId && String(c.assigneeId || '') !== myId) continue;
        nextMap.set(c.id, Number(c.unreadStaff) || 0);
      }
      unreadMapRef.current = nextMap;
      listReadyRef.current = true;

      setList(chats);
      setCounts(data.counts || { queued: 0, mine: 0, open: 0 });
      if (data.capabilities && typeof data.capabilities === 'object') {
        setServerCaps(data.capabilities);
      }
      setError('');
    } catch (err) {
      setError(err.message || 'Falha ao listar');
      onAuthError?.(err);
    } finally {
      setLoadingList(false);
    }
  }, [filter, onAuthError]);

  useEffect(() => {
    // Troca de filtro: não tocar blim no primeiro carregamento da lista
    listReadyRef.current = false;
    unreadMapRef.current = new Map();
    loadList();
    const id = setInterval(loadList, 8000);
    return () => clearInterval(id);
  }, [loadList]);

  const openChat = useCallback(
    async (id) => {
      setSelectedId(id);
      setBusy(true);
      setError('');
      try {
        const [sync, ctx] = await Promise.all([
          syncSupportChat(id, { sinceSeq: 0, role: 'staff' }),
          supportChatContext(id, { refresh: true }),
        ]);
        setChat(sync.chat || ctx.chat);
        const msgs = sync.messages || [];
        setMessages(msgs);
        const maxSeq = msgs.reduce(
          (m, msg) => Math.max(m, msg.seq || 0),
          sync.chat?.lastSeq || 0
        );
        resetRef.current(maxSeq);
        const ctxData = ctx.context || sync.chat?.contextSnapshot || null;
        setContext(ctxData);
        setEditNick(ctxData?.subscription?.minecraftNick || '');
        setEditPlan(ctxData?.subscription?.planId || 'origin');
        setGrantNick(ctxData?.linkedProfile?.minecraftNick || '');
        setSuccessMsg('');
        loadList();
        supportChatVipAction({ chatId: id, action: 'list_history' })
          .then((h) => setHistory(h.history || []))
          .catch(() => setHistory([]));
      } catch (err) {
        setError(err.message || 'Falha ao abrir chat');
        onAuthError?.(err);
      } finally {
        setBusy(false);
      }
    },
    [loadList, onAuthError]
  );

  useEffect(() => {
    if (!focusChatId) return;
    openChat(focusChatId).finally(() => {
      onFocusChatConsumed?.();
    });
  }, [focusChatId, openChat, onFocusChatConsumed]);

  const onMessages = useCallback((incoming) => {
    if (Array.isArray(incoming) && incoming.some((m) => m.authorType === 'user')) {
      playNotifyBeep();
    }
    setMessages((prev) => mergeMessages(prev, incoming));
  }, []);

  const onChatUpdate = useCallback((next) => {
    setChat(next);
    setList((prev) =>
      prev.map((c) => (c.id === next.id ? { ...c, ...next } : c))
    );
  }, []);

  const { markActivity, reset } = useSupportSync({
    chatId: selectedId,
    role: 'staff',
    enabled: Boolean(selectedId),
    onMessages,
    onChat: onChatUpdate,
  });

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, chat?.typing]);

  resetRef.current = reset;

  const canRunAction = (action) => {
    switch (action) {
      case 'claim':
        return canClaim;
      case 'release':
        return canRelease;
      case 'close':
        return canClose;
      case 'transfer':
        return canTransfer;
      case 'reopen':
      case 'priority':
      case 'internal_note':
      case 'tags':
        return canEdit;
      default:
        return canEdit;
    }
  };

  const runAction = async (payload) => {
    if (!canRunAction(payload?.action)) return;
    setBusy(true);
    setError('');
    try {
      const data = await supportChatAction(payload);
      if (data.chat) {
        setChat(data.chat);
        onChatUpdate(data.chat);
      }
      if (data.message) {
        setMessages((prev) => mergeMessages(prev, [data.message]));
        reset(data.message.seq);
      }
      markActivity();
      loadList();
    } catch (err) {
      setError(err.message || 'Falha na ação');
      onAuthError?.(err);
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedId || busy) return;
    if (noteMode ? !canEdit : !canChat) return;
    const text = draft.trim();
    if (!text && !pendingImage) return;
    if (noteMode && !text) return;
    const image = pendingImage;
    setDraft('');
    setPendingImage(null);
    setBusy(true);
    try {
      if (noteMode) {
        const data = await supportChatAction({
          action: 'internal_note',
          chatId: selectedId,
          body: text,
        });
        if (data.message) {
          setMessages((prev) => mergeMessages(prev, [data.message]));
        }
        if (data.chat) setChat(data.chat);
      } else {
        const data = await sendSupportMessage(selectedId, text, {
          role: 'staff',
          attachment: image
            ? { mime: image.mime, data: image.data, name: image.name }
            : null,
        });
        if (data.message) {
          setMessages((prev) => mergeMessages(prev, [data.message]));
          reset(data.message.seq);
        }
        if (data.chat) setChat(data.chat);
      }
      markActivity();
      loadList();
    } catch (err) {
      setDraft(text);
      if (image) setPendingImage(image);
      setError(err.message || 'Falha ao enviar');
      onAuthError?.(err);
    } finally {
      setBusy(false);
    }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || noteMode) return;
    setBusy(true);
    setError('');
    try {
      setPendingImage(await compressImageFile(file));
    } catch (err) {
      setError(err.message || 'Falha ao preparar imagem');
    } finally {
      setBusy(false);
    }
  };

  const setScheduleDay = (day, next) => {
    setSettingsDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        schedule: {
          ...(prev.schedule || {}),
          [day]: next,
          [String(day)]: next,
        },
      };
    });
  };

  const applyHoursToWeekdays = () => {
    const mon = scheduleDay(settingsDraft?.schedule, 1) || {
      start: '10:00',
      end: '22:00',
    };
    setSettingsDraft((prev) => {
      if (!prev) return prev;
      const schedule = { ...(prev.schedule || {}) };
      for (let d = 1; d <= 5; d += 1) {
        schedule[d] = { ...mon };
        schedule[String(d)] = { ...mon };
      }
      return { ...prev, schedule };
    });
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    if (!settingsDraft || !canEdit) return;
    setSettingsSaving(true);
    setError('');
    try {
      const schedule = {};
      for (let d = 0; d <= 6; d += 1) {
        const slot = scheduleDay(settingsDraft.schedule, d);
        schedule[d] = slot
          ? {
              start: String(slot.start || '10:00').slice(0, 5),
              end: String(slot.end || '22:00').slice(0, 5),
            }
          : null;
      }
      const data = await updateSupportSettings({
        hoursEnabled: Boolean(settingsDraft.hoursEnabled),
        allowStartOutsideHours: settingsDraft.allowStartOutsideHours !== false,
        slaQueueMinutes: Number(settingsDraft.slaQueueMinutes) || 10,
        afterHoursMessage: settingsDraft.afterHoursMessage || '',
        timezone: settingsDraft.timezone || 'America/Sao_Paulo',
        schedule,
        discordLogEnabled: Boolean(settingsDraft.discordLogEnabled),
        discordLogChannelId: settingsDraft.discordLogChannelId || null,
        discordLogMentionRoleId:
          settingsDraft.discordLogMentionRoleId || null,
        discordLogEvents: {
          queued: settingsDraft.discordLogEvents?.queued !== false,
          claim: settingsDraft.discordLogEvents?.claim !== false,
          release: settingsDraft.discordLogEvents?.release !== false,
          transfer: settingsDraft.discordLogEvents?.transfer !== false,
          close: settingsDraft.discordLogEvents?.close !== false,
          reopen: settingsDraft.discordLogEvents?.reopen !== false,
        },
      });
      setSettingsDraft(data.settings);
      setSlaQueueMinutes(data.settings?.slaQueueMinutes || 10);
      setSuccessMsg('Configurações do Chat VIP salvas.');
    } catch (err) {
      setError(err.message || 'Falha ao salvar configurações');
      onAuthError?.(err);
    } finally {
      setSettingsSaving(false);
    }
  };

  const onDraftChange = (value) => {
    setDraft(value);
    if (!selectedId || noteMode || !canChat) return;
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      pingSupportTyping(selectedId, { role: 'staff' }).catch(() => {});
    }, 500);
  };

  const handleVipAction = async (action, extra = {}) => {
    if (!selectedId) return;
    setBusy(true);
    setError('');
    setSuccessMsg('');
    try {
      const data = await supportChatVipAction({
        chatId: selectedId,
        action,
        ...extra,
      });
      if (data.chat) setChat(data.chat);
      if (data.context) {
        setContext(data.context);
        setEditNick(data.context?.subscription?.minecraftNick || editNick);
        setEditPlan(data.context?.subscription?.planId || editPlan);
      }
      if (data.message) {
        setMessages((prev) => mergeMessages(prev, [data.message]));
        resetRef.current(data.message.seq);
      }
      if (data.messageText) setSuccessMsg(data.messageText);
      if (action === 'list_history') setHistory(data.history || []);
      markActivity();
      loadList();
    } catch (err) {
      setError(err.message || 'Falha na ação VIP');
      onAuthError?.(err);
    } finally {
      setBusy(false);
      setConfirmVip(null);
    }
  };

  const insertDraft = (text) => {
    setNoteMode(false);
    setDraft(text);
  };

  const clearSelection = () => {
    setSelectedId(null);
    setChat(null);
    setMessages([]);
    setContext(null);
    resetRef.current(0);
  };

  const executeDelete = async () => {
    if (!canDelete || !confirmDelete) return;
    setBusy(true);
    setError('');
    try {
      const payload =
        confirmDelete.mode === 'all'
          ? { allClosed: true }
          : { chatIds: [confirmDelete.chatId] };
      const data = await deleteSupportChats(payload);
      setConfirmDelete(null);
      if (
        confirmDelete.mode === 'all' ||
        (confirmDelete.chatId && confirmDelete.chatId === selectedId)
      ) {
        clearSelection();
      }
      await loadList();
      if (data.message) setError(''); // sucesso silencioso na lista
    } catch (err) {
      setError(err.message || 'Falha ao excluir');
      onAuthError?.(err);
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
    } catch {
      /* ignore */
    }
  };

  const sub = context?.subscription;
  const profile = context?.linkedProfile;
  const selfId = adminService.getAdminUser()?.id;
  const otherTyping =
    chat?.typing &&
    chat.typing.by &&
    chat.typing.byName &&
    String(chat.typing.by) !== String(selfId || '');

  const memberOptions = useMemo(() => {
    return (guildMembers || [])
      .map((m) => ({
        id: m.id || m.user?.id || m.userId,
        name:
          m.displayName ||
          m.global_name ||
          m.username ||
          m.user?.global_name ||
          m.user?.username ||
          m.nick ||
          m.userId,
      }))
      .filter((m) => m.id);
  }, [guildMembers]);

  return (
    <section
      className="admin-section sd-root"
      onPointerDown={unlockNotifyBeep}
      onKeyDown={unlockNotifyBeep}
    >
      <header className="admin-section__head sd-head">
        <div className="sd-head__title">
          <h1>
            <FaHeadset aria-hidden="true" />
            Chat VIP
          </h1>
          <p>
            Atendimento ao vivo da loja VIP — fila, contexto da assinatura e
            ações da equipe em um só lugar.
          </p>
        </div>
        <div className="sd-head__aside">
          <div className="sd-counts" aria-label="Resumo da fila">
            <span className="sd-count">
              Fila <strong>{counts.queued}</strong>
            </span>
            <span className="sd-count">
              Abertos <strong>{counts.open}</strong>
            </span>
            <span className="sd-count">
              Meus <strong>{counts.mine}</strong>
            </span>
          </div>
          <PermBtn
            allowed={canReport}
            lockedHint="Sem permissão para abrir o relatório"
            className={`admin-btn admin-btn--ghost admin-btn--sm${
              showStats ? ' is-active' : ''
            }`}
            onClick={() => {
              setShowStats((v) => !v);
              setShowSettings(false);
            }}
          >
            <FaChartBar aria-hidden="true" /> Relatório
          </PermBtn>
          <PermBtn
            allowed={canEdit}
            lockedHint="Sem permissão para alterar horário/SLA"
            className={`admin-btn admin-btn--ghost admin-btn--sm${
              showSettings ? ' is-active' : ''
            }`}
            onClick={() => {
              setShowSettings((v) => {
                const next = !v;
                if (next) loadChannels();
                return next;
              });
              setShowStats(false);
            }}
          >
            <FaCog aria-hidden="true" /> Horário / Logs
          </PermBtn>
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => loadList()}
            disabled={loadingList}
          >
            <FaSync className={loadingList ? 'admin-spin' : ''} aria-hidden="true" />
            Atualizar
          </button>
          {filter === 'closed' && list.length > 0 && (
            <PermBtn
              allowed={canDelete}
              lockedHint="Sem permissão para excluir atendimentos"
              className="admin-btn admin-btn--ghost admin-btn--sm"
              disabled={busy}
              onClick={() =>
                setConfirmDelete({
                  mode: 'all',
                  title: 'Limpar atendimentos fechados',
                  message:
                    'Excluir permanentemente todos os atendimentos fechados? As mensagens também serão apagadas. Esta ação não pode ser desfeita.',
                })
              }
            >
              <FaTrash aria-hidden="true" /> Limpar fechados
            </PermBtn>
          )}
        </div>
      </header>

      {showStats && canReport && (
        <div className="sd-insight">
          <div className="sd-insight__head">
            <div className="sd-insight__title">
              <span className="sd-insight__icon" aria-hidden="true">
                <FaChartBar />
              </span>
              <div>
                <h3>Relatório operacional</h3>
                <p>Últimos 14 dias · desempenho da fila e atendimento</p>
              </div>
            </div>
            <button
              type="button"
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={loadStats}
              disabled={statsLoading}
            >
              <FaSync className={statsLoading ? 'admin-spin' : ''} aria-hidden="true" />
              Atualizar
            </button>
          </div>

          {statsLoading && !stats ? (
            <p className="sd-insight__empty">Carregando métricas…</p>
          ) : stats ? (
            <>
              <div className="sd-kpi-grid">
                <article className="sd-kpi">
                  <span className="sd-kpi__icon sd-kpi__icon--violet">
                    <FaComments aria-hidden="true" />
                  </span>
                  <div>
                    <span className="sd-kpi__label">Abertos</span>
                    <strong className="sd-kpi__value">
                      {stats.summary?.opened ?? 0}
                    </strong>
                  </div>
                </article>
                <article className="sd-kpi">
                  <span className="sd-kpi__icon sd-kpi__icon--ok">
                    <FaDoorOpen aria-hidden="true" />
                  </span>
                  <div>
                    <span className="sd-kpi__label">Fechados</span>
                    <strong className="sd-kpi__value">
                      {stats.summary?.closed ?? 0}
                    </strong>
                  </div>
                </article>
                <article className="sd-kpi">
                  <span className="sd-kpi__icon sd-kpi__icon--warn">
                    <FaHourglassHalf aria-hidden="true" />
                  </span>
                  <div>
                    <span className="sd-kpi__label">Média na fila</span>
                    <strong className="sd-kpi__value">
                      {formatMinutes(stats.summary?.avgQueueMinutes)}
                    </strong>
                  </div>
                </article>
                <article className="sd-kpi">
                  <span className="sd-kpi__icon sd-kpi__icon--ok">
                    <FaClock aria-hidden="true" />
                  </span>
                  <div>
                    <span className="sd-kpi__label">Média em atendimento</span>
                    <strong className="sd-kpi__value">
                      {formatMinutes(stats.summary?.avgHandleMinutes)}
                    </strong>
                  </div>
                </article>
                <article className="sd-kpi">
                  <span className="sd-kpi__icon sd-kpi__icon--gold">
                    <FaStar aria-hidden="true" />
                  </span>
                  <div>
                    <span className="sd-kpi__label">Nota média</span>
                    <strong className="sd-kpi__value">
                      {stats.summary?.avgRating != null
                        ? stats.summary.avgRating.toFixed(1)
                        : '—'}
                    </strong>
                    <small className="sd-kpi__meta">
                      {stats.summary?.rated
                        ? `${stats.summary.rated} avaliação(ões)`
                        : 'Sem avaliações'}
                    </small>
                  </div>
                </article>
                <article className="sd-kpi">
                  <span className="sd-kpi__icon sd-kpi__icon--danger">
                    <FaBolt aria-hidden="true" />
                  </span>
                  <div>
                    <span className="sd-kpi__label">SLA da fila</span>
                    <strong className="sd-kpi__value">
                      {stats.slaQueueMinutes || slaQueueMinutes} min
                    </strong>
                  </div>
                </article>
              </div>

              <div className="sd-insight__split">
                <div className="sd-insight__block">
                  <h4>Volume por tópico</h4>
                  <ul className="sd-bars">
                    {(() => {
                      const topics = stats.volumeByTopic || [];
                      const max = Math.max(1, ...topics.map((t) => t.count || 0));
                      return topics.map((t) => (
                        <li key={t.topic}>
                          <div className="sd-bars__row">
                            <span>{t.label}</span>
                            <strong>{t.count}</strong>
                          </div>
                          <div className="sd-bars__track" aria-hidden="true">
                            <span
                              className="sd-bars__fill"
                              style={{
                                width: `${Math.round(((t.count || 0) / max) * 100)}%`,
                              }}
                            />
                          </div>
                        </li>
                      ));
                    })()}
                  </ul>
                </div>
                <div className="sd-insight__block">
                  <h4>Volume por dia</h4>
                  <ul className="sd-day-list">
                    {(stats.volumeByDay || []).length === 0 ? (
                      <li className="sd-day-list__empty">Sem movimentação</li>
                    ) : (
                      (stats.volumeByDay || []).map((d) => (
                        <li key={d.date}>
                          <span className="sd-day-list__date">
                            <FaRegCalendarAlt aria-hidden="true" />
                            {formatReportDay(d.date)}
                          </span>
                          <span className="sd-day-list__count">{d.count}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <p className="sd-insight__empty">Sem dados no período.</p>
          )}
        </div>
      )}

      {showSettings && !settingsDraft && (
        <div className="sd-insight">
          <p className="sd-insight__empty">Carregando configurações…</p>
        </div>
      )}

      {showSettings && settingsDraft && (
        <form className="sd-insight sd-insight--settings" onSubmit={saveSettings}>
          <div className="sd-insight__head">
            <div className="sd-insight__title">
              <span className="sd-insight__icon" aria-hidden="true">
                <FaClock />
              </span>
              <div>
                <h3>Horário, SLA e logs Discord</h3>
                <p>Agenda de atendimento e avisos no canal de logs (Container V2)</p>
              </div>
            </div>
            <button
              type="submit"
              className="admin-btn admin-btn--sm"
              disabled={settingsSaving}
            >
              {settingsSaving ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>

          <div className="sd-settings-grid">
            <div className="sd-settings-col">
              <div className="sd-toggle-card">
                <label className="sd-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(settingsDraft.hoursEnabled)}
                    onChange={(e) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        hoursEnabled: e.target.checked,
                      }))
                    }
                  />
                  <span className="sd-toggle__ui" aria-hidden="true" />
                  <span className="sd-toggle__copy">
                    <strong>Ativar horário</strong>
                    <small>Controla aviso e bloqueio fora do expediente</small>
                  </span>
                </label>
                <label className="sd-toggle">
                  <input
                    type="checkbox"
                    checked={settingsDraft.allowStartOutsideHours !== false}
                    onChange={(e) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        allowStartOutsideHours: e.target.checked,
                      }))
                    }
                  />
                  <span className="sd-toggle__ui" aria-hidden="true" />
                  <span className="sd-toggle__copy">
                    <strong>Permitir abrir fora do horário</strong>
                    <small>Jogador ainda pode iniciar, com aviso no chat</small>
                  </span>
                </label>
              </div>

              <label className="sd-field">
                Fuso horário
                <select
                  className="sd-select"
                  value={settingsDraft.timezone || 'America/Sao_Paulo'}
                  onChange={(e) =>
                    setSettingsDraft((prev) => ({
                      ...prev,
                      timezone: e.target.value,
                    }))
                  }
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="sd-field">
                SLA da fila (minutos)
                <input
                  type="number"
                  min={1}
                  max={180}
                  value={settingsDraft.slaQueueMinutes || 10}
                  onChange={(e) =>
                    setSettingsDraft((prev) => ({
                      ...prev,
                      slaQueueMinutes: Number(e.target.value) || 10,
                    }))
                  }
                />
                <small className="sd-field__hint">
                  Após esse tempo, o atendimento na fila fica destacado em vermelho.
                </small>
              </label>

              <label className="sd-field">
                Aviso fora do expediente
                <textarea
                  rows={4}
                  maxLength={500}
                  value={settingsDraft.afterHoursMessage || ''}
                  onChange={(e) =>
                    setSettingsDraft((prev) => ({
                      ...prev,
                      afterHoursMessage: e.target.value,
                    }))
                  }
                  placeholder="Mensagem exibida ao jogador fora do horário…"
                />
              </label>

              <div className="sd-discord-log">
                <div className="sd-schedule__head">
                  <div>
                    <h4>Logs no Discord</h4>
                    <p>
                      Container V2 com botão para abrir o atendimento no painel
                    </p>
                  </div>
                </div>
                <div className="sd-toggle-card">
                  <label className="sd-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(settingsDraft.discordLogEnabled)}
                      onChange={(e) =>
                        setSettingsDraft((prev) => ({
                          ...prev,
                          discordLogEnabled: e.target.checked,
                        }))
                      }
                    />
                    <span className="sd-toggle__ui" aria-hidden="true" />
                    <span className="sd-toggle__copy">
                      <strong>Enviar avisos no Discord</strong>
                      <small>
                        Fila, assumir, liberar, transferir, fechar e duração
                      </small>
                    </span>
                  </label>
                </div>
                <label className="sd-field">
                  Canal de logs
                  <div className="sd-field__row">
                    <select
                      className="sd-select"
                      value={settingsDraft.discordLogChannelId || ''}
                      onFocus={() => {
                        if (!logChannelList.length && !logChannelsBusy) {
                          loadChannels();
                        }
                      }}
                      onChange={(e) =>
                        setSettingsDraft((prev) => ({
                          ...prev,
                          discordLogChannelId: e.target.value || null,
                        }))
                      }
                    >
                      <option value="">
                        {logChannelsBusy
                          ? 'Carregando canais…'
                          : 'Selecione um canal…'}
                      </option>
                      {logChannelList.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.parentName
                            ? `#${ch.name} (${ch.parentName})`
                            : `#${ch.name}`}
                        </option>
                      ))}
                      {settingsDraft.discordLogChannelId &&
                        !logChannelList.some(
                          (ch) =>
                            String(ch.id) ===
                            String(settingsDraft.discordLogChannelId)
                        ) && (
                          <option value={settingsDraft.discordLogChannelId}>
                            Canal salvo ({settingsDraft.discordLogChannelId})
                          </option>
                        )}
                    </select>
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      disabled={logChannelsBusy}
                      onClick={() => loadChannels()}
                      title="Recarregar canais"
                    >
                      <FaSync
                        className={logChannelsBusy ? 'admin-spin' : ''}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  {channelsError ? (
                    <small className="sd-field__hint sd-field__hint--error">
                      {channelsError}
                    </small>
                  ) : (
                    <small className="sd-field__hint">
                      {logChannelList.length
                        ? `${logChannelList.length} canais disponíveis. O bot precisa poder enviar mensagens no canal escolhido.`
                        : 'Abra o seletor ou clique em recarregar para listar os canais.'}
                    </small>
                  )}
                </label>
                <label className="sd-field">
                  Cargo para mencionar na fila
                  <select
                    className="sd-select"
                    value={settingsDraft.discordLogMentionRoleId || ''}
                    onFocus={() => {
                      if (!logRoleList.length && !logChannelsBusy) {
                        loadChannels();
                      }
                    }}
                    onChange={(e) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        discordLogMentionRoleId: e.target.value || null,
                      }))
                    }
                  >
                    <option value="">
                      {logChannelsBusy
                        ? 'Carregando cargos…'
                        : 'Nenhum (sem menção)'}
                    </option>
                    {logRoleList.map((role) => (
                      <option key={role.id} value={role.id}>
                        @{role.name}
                      </option>
                    ))}
                    {settingsDraft.discordLogMentionRoleId &&
                      !logRoleList.some(
                        (r) =>
                          String(r.id) ===
                          String(settingsDraft.discordLogMentionRoleId)
                      ) && (
                        <option value={settingsDraft.discordLogMentionRoleId}>
                          Cargo salvo ({settingsDraft.discordLogMentionRoleId})
                        </option>
                      )}
                  </select>
                  <small className="sd-field__hint">
                    Mencionado só quando um atendimento entra na fila — útil para
                    quem está offline do painel receber notificação no Discord.
                    O cargo precisa estar com “Mencionar este cargo” permitido.
                  </small>
                </label>
                <div className="sd-log-events" role="group" aria-label="Eventos">
                  {DISCORD_LOG_EVENT_OPTIONS.map((ev) => {
                    const checked =
                      settingsDraft.discordLogEvents?.[ev.id] !== false;
                    return (
                      <label
                        key={ev.id}
                        className={`sd-log-event${checked ? ' is-on' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setSettingsDraft((prev) => ({
                              ...prev,
                              discordLogEvents: {
                                ...(prev.discordLogEvents || {}),
                                [ev.id]: e.target.checked,
                              },
                            }))
                          }
                        />
                        {ev.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="sd-settings-col">
              <div className="sd-schedule">
                <div className="sd-schedule__head">
                  <div>
                    <h4>Agenda semanal</h4>
                    <p>Marque os dias abertos e o intervalo de atendimento</p>
                  </div>
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost admin-btn--sm"
                    onClick={applyHoursToWeekdays}
                  >
                    Copiar seg. → úteis
                  </button>
                </div>
                <div className="sd-schedule__rows">
                  {WEEKDAY_ROWS.map(({ day, label }) => {
                    const slot = scheduleDay(settingsDraft.schedule, day);
                    const open = Boolean(slot);
                    return (
                      <div
                        key={day}
                        className={`sd-schedule__row${open ? ' is-open' : ''}`}
                      >
                        <label className="sd-schedule__day">
                          <input
                            type="checkbox"
                            checked={open}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setScheduleDay(day, {
                                  start: '10:00',
                                  end: '22:00',
                                });
                              } else {
                                setScheduleDay(day, null);
                              }
                            }}
                          />
                          <span>{label}</span>
                        </label>
                        {open ? (
                          <div className="sd-schedule__times">
                            <input
                              type="time"
                              className="sd-schedule__time"
                              value={slot?.start || '10:00'}
                              onChange={(e) =>
                                setScheduleDay(day, {
                                  start: e.target.value,
                                  end: slot?.end || '22:00',
                                })
                              }
                            />
                            <span className="sd-schedule__sep">–</span>
                            <input
                              type="time"
                              className="sd-schedule__time"
                              value={slot?.end || '22:00'}
                              onChange={(e) =>
                                setScheduleDay(day, {
                                  start: slot?.start || '10:00',
                                  end: e.target.value,
                                })
                              }
                            />
                          </div>
                        ) : (
                          <span className="sd-schedule__closed">Fechado</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </form>
      )}

      <div className="sd-desk">
        <aside className="sd-col sd-col--queue">
          <div className="sd-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`sd-chip${filter === f.id ? ' is-active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="sd-search">
            <input
              type="search"
              placeholder="Buscar nick, Discord…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                queryRef.current = e.target.value;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadList();
              }}
            />
          </div>
          <div className="sd-queue">
            {list.length === 0 && (
              <p className="sd-thread__empty">Nenhum chat nesta fila.</p>
            )}
            {list.map((c) => {
              const timer = chatTimerInfo(c, nowMs, slaQueueMinutes);
              const shortTimer = timer
                ? timer.label.replace(/^(Em atendimento|Na fila|Durou)\s/, '')
                : '';
              const queueAvatar = discordAvatarUrl(c.userId, c.avatar, 48);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`sd-queue-item${
                    selectedId === c.id ? ' is-active' : ''
                  }`}
                  onClick={() => openChat(c.id)}
                >
                  <div className="sd-queue-item__top">
                    <span className="sd-queue-item__who">
                      {queueAvatar && (
                        <img
                          className="sd-queue-item__avatar"
                          src={queueAvatar}
                          alt=""
                          width={22}
                          height={22}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      {c.globalName || c.username || c.userId}
                    </span>
                    {c.unreadStaff > 0 && (
                      <span className="sd-badge">{c.unreadStaff}</span>
                    )}
                  </div>
                  <div className="sd-queue-item__meta">
                    <span>{c.topicLabel}</span>
                    <span>{c.statusLabel}</span>
                    {timer && (
                      <span
                        className={`sd-timer sd-timer--${timer.kind}${
                          timer.breach ? ' sd-timer--breach' : ''
                        }`}
                        title={
                          timer.breach
                            ? `${timer.label} · SLA estourado (>${slaQueueMinutes} min)`
                            : timer.label
                        }
                      >
                        {shortTimer}
                      </span>
                    )}
                    {c.priority && c.priority !== 'normal' && (
                      <span className={`sd-prio sd-prio--${c.priority}`}>
                        {c.priority}
                      </span>
                    )}
                  </div>
                  <div className="sd-queue-item__preview">
                    {c.lastMessagePreview || '—'}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="sd-col sd-col--thread">
          {!selectedId || !chat ? (
            <div className="sd-thread__empty">
              Selecione um atendimento na fila para responder ao vivo.
            </div>
          ) : (
            <div className="sd-thread">
              <div className="sd-col__head">
                <h2>
                  {chat.globalName || chat.username}
                  <span className="sd-col__sub">
                    {chat.topicLabel} · {chat.statusLabel}
                    {selectedTimer ? (
                      <>
                        {' · '}
                        <span
                          className={`sd-timer sd-timer--${selectedTimer.kind}${
                            selectedTimer.breach ? ' sd-timer--breach' : ''
                          }`}
                        >
                          {selectedTimer.label}
                          {selectedTimer.breach ? ' · SLA' : ''}
                        </span>
                      </>
                    ) : null}
                  </span>
                </h2>
                <div className="sd-col__tools">
                  {!chat.assigneeId && chat.status !== 'closed' && (
                    <PermBtn
                      allowed={canClaim}
                      lockedHint="Sem permissão para assumir"
                      className="admin-btn admin-btn--sm"
                      disabled={busy}
                      onClick={() =>
                        runAction({ action: 'claim', chatId: chat.id })
                      }
                    >
                      <FaUserCheck /> Assumir
                    </PermBtn>
                  )}
                  {chat.assigneeId && chat.status !== 'closed' && (
                    <PermBtn
                      allowed={canRelease}
                      lockedHint="Sem permissão para liberar"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      disabled={busy}
                      onClick={() =>
                        runAction({ action: 'release', chatId: chat.id })
                      }
                    >
                      Liberar
                    </PermBtn>
                  )}
                  {chat.status !== 'closed' ? (
                    <PermBtn
                      allowed={canClose}
                      lockedHint="Sem permissão para fechar atendimento"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      disabled={busy}
                      onClick={() =>
                        runAction({
                          action: 'close',
                          chatId: chat.id,
                          resolution,
                        })
                      }
                    >
                      <FaDoorOpen /> Fechar
                    </PermBtn>
                  ) : (
                    <>
                      <PermBtn
                        allowed={canEdit}
                        lockedHint="Sem permissão para reabrir"
                        className="admin-btn admin-btn--sm"
                        disabled={busy}
                        onClick={() =>
                          runAction({ action: 'reopen', chatId: chat.id })
                        }
                      >
                        Reabrir
                      </PermBtn>
                      <PermBtn
                        allowed={canDelete}
                        lockedHint="Sem permissão para excluir"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        disabled={busy}
                        onClick={() =>
                          setConfirmDelete({
                            mode: 'one',
                            chatId: chat.id,
                            title: 'Excluir atendimento',
                            message: `Excluir permanentemente o chat de ${
                              chat.globalName || chat.username || 'jogador'
                            }? As mensagens serão apagadas.`,
                          })
                        }
                      >
                        <FaTrash aria-hidden="true" /> Excluir
                      </PermBtn>
                    </>
                  )}
                </div>
              </div>

              <div className="sd-msgs" ref={listRef}>
                {messages.map((m) => {
                  const tone =
                    m.visibility === 'internal' ? 'internal' : m.authorType;
                  const avatarUrl = messageAvatarUrl(m, chat);
                  const showBody =
                    m.body &&
                    !(
                      m.attachment &&
                      /^\[Imagem(?::[^\]]*)?\]$/i.test(String(m.body).trim())
                    );
                  return (
                    <div
                      key={m.seq}
                      className={`sd-msg-row sd-msg-row--${tone}`}
                    >
                      {avatarUrl && (
                        <img
                          className="sd-avatar"
                          src={avatarUrl}
                          alt=""
                          width={28}
                          height={28}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <div className={`sd-msg sd-msg--${tone}`}>
                        {m.authorType !== 'system' && (
                          <span className="sd-msg__meta">
                            {m.visibility === 'internal'
                              ? 'Nota interna · '
                              : ''}
                            {m.authorName || m.authorType}
                          </span>
                        )}
                        {m.attachment && (
                          <SupportAttachmentImage
                            attachment={m.attachment}
                            role="staff"
                            className="sd-attach-img"
                          />
                        )}
                        {showBody ? m.body : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              {chat.rating != null && (
                <div className="sd-rating-banner">
                  <FaStar aria-hidden="true" />
                  Avaliação do jogador: <strong>{chat.rating}/5</strong>
                  {chat.ratingComment ? ` — ${chat.ratingComment}` : ''}
                </div>
              )}

              <div className="sd-typing" aria-live="polite">
                {otherTyping ? `${chat.typing.byName} está digitando…` : ''}
              </div>

              {error && <p className="sd-error">{error}</p>}

              {chat.status !== 'closed' && !canChat && canEdit && (
                <div className="sd-composer sd-perm-banner">
                  <FaLock className="sd-perm-banner__icon" aria-hidden="true" />
                  <p className="sd-perm-banner__title">
                    Sem permissão de <strong>Chat</strong>
                  </p>
                  <p className="sd-perm-banner__text">
                    Você pode usar notas internas, mas não enviar mensagens ao
                    jogador.
                  </p>
                </div>
              )}
              {chat.status !== 'closed' && (canChat || canEdit) && (
                <>
                  {canChat && !noteMode && (
                    <div className="sd-templates">
                      {QUICK_REPLIES.map((q) => (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => {
                            setNoteMode(false);
                            setDraft(q.text);
                          }}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <form className="sd-composer" onSubmit={handleSend}>
                    {pendingImage && (
                      <div className="sd-pending-img">
                        <img src={pendingImage.dataUrl} alt="" />
                        <button
                          type="button"
                          onClick={() => setPendingImage(null)}
                          aria-label="Remover imagem"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <textarea
                      value={draft}
                      maxLength={2000}
                      onChange={(e) => onDraftChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!busy && (draft.trim() || pendingImage)) {
                            handleSend(e);
                          }
                        }
                      }}
                      placeholder={
                        noteMode
                          ? 'Nota interna (só a equipe vê)… Enter envia'
                          : 'Responder ou anexar comprovante… Enter envia'
                      }
                      disabled={noteMode ? !canEdit : !canChat}
                    />
                    <div className="sd-composer__actions">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        hidden
                        onChange={onPickImage}
                      />
                      {canChat && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={busy || noteMode}
                          onClick={() => fileRef.current?.click()}
                          title="Anexar imagem"
                        >
                          <FaImage /> Imagem
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          className={`admin-btn admin-btn--ghost admin-btn--sm${
                            noteMode ? ' is-active' : ''
                          }`}
                          onClick={() => {
                            setNoteMode((v) => !v);
                            setPendingImage(null);
                          }}
                        >
                          <FaStickyNote /> {noteMode ? 'Nota ON' : 'Nota'}
                        </button>
                      )}
                      <label className="sd-resolution">
                        Resolução
                        <input
                          value={resolution}
                          onChange={(e) => setResolution(e.target.value)}
                        />
                      </label>
                      <button
                        type="submit"
                        className="admin-btn admin-btn--sm"
                        disabled={
                          busy ||
                          (!draft.trim() && !pendingImage) ||
                          (noteMode ? !canEdit : !canChat)
                        }
                      >
                        <FaPaperPlane /> Enviar
                      </button>
                    </div>
                  </form>
                </>
              )}
              {chat.status !== 'closed' && !canChat && !canEdit && (
                <div className="sd-composer sd-perm-banner">
                  <FaLock className="sd-perm-banner__icon" aria-hidden="true" />
                  <p className="sd-perm-banner__title">
                    Sem permissão de <strong>Chat</strong>
                  </p>
                  <p className="sd-perm-banner__text">
                    Não é possível enviar mensagens neste atendimento.
                  </p>
                  {serverCaps?.actions?.length ? (
                    <div className="sd-perm-banner__meta">
                      <span className="sd-perm-banner__meta-label">
                        Ações neste login
                      </span>
                      <div className="sd-perm-banner__chips">
                        {serverCaps.actions.map((action) => (
                          <span key={action} className="sd-perm-banner__chip">
                            {action}
                          </span>
                        ))}
                      </div>
                      <p className="sd-perm-banner__hint">
                        Confira o cargo/usuário em Permissões e atualize a lista.
                      </p>
                    </div>
                  ) : (
                    <p className="sd-perm-banner__hint">
                      Em Permissões → Chat VIP, marque a ação <strong>Chat</strong>,
                      salve e clique em Atualizar.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="sd-col sd-col--context">
          <div className="sd-col__head">
            <h3>
              <FaCrown aria-hidden="true" /> Contexto
            </h3>
            <button
              type="button"
              className="admin-btn admin-btn--ghost admin-btn--sm sd-icon-btn"
              disabled={!selectedId || busy}
              onClick={() => handleVipAction('refresh_context')}
              aria-label="Atualizar contexto"
            >
              <FaSync aria-hidden="true" />
            </button>
          </div>
          <div className="sd-context">
            {!chat ? (
              <p className="sd-thread__empty">
                Abra um chat para ver VIP e conta.
              </p>
            ) : (
              <>
                <div className="sd-card">
                  <h4>Jogador</h4>
                  <dl className="sd-kv">
                    <dt>Nome</dt>
                    <dd>{chat.globalName || chat.username}</dd>
                    <dt>Discord ID</dt>
                    <dd>
                      <span>{chat.userId}</span>
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm sd-icon-btn"
                        onClick={() => copyText(chat.userId)}
                        aria-label="Copiar Discord ID"
                      >
                        <FaCopy aria-hidden="true" />
                      </button>
                    </dd>
                    <dt>Prioridade</dt>
                    <dd>
                      {canEdit ? (
                        <select className="sd-select"
                          value={chat.priority || 'normal'}
                          onChange={(e) =>
                            runAction({
                              action: 'priority',
                              chatId: chat.id,
                              priority: e.target.value,
                            })
                          }
                        >
                          <option value="low">Baixa</option>
                          <option value="normal">Normal</option>
                          <option value="high">Alta</option>
                          <option value="urgent">Urgente</option>
                        </select>
                      ) : (
                        chat.priority
                      )}
                    </dd>
                    <dt>Agente</dt>
                    <dd>{chat.assigneeName || '—'}</dd>
                  </dl>
                </div>

                <div className="sd-card">
                  <h4>Assinatura VIP</h4>
                  {sub ? (
                    <dl className="sd-kv">
                      <dt>Plano</dt>
                      <dd>{sub.planName || sub.planId}</dd>
                      <dt>Status</dt>
                      <dd>{sub.status}</dd>
                      <dt>Nick</dt>
                      <dd>
                        <span>{sub.minecraftNick || '—'}</span>
                        {sub.minecraftNick && (
                          <button
                            type="button"
                            className="admin-btn admin-btn--ghost admin-btn--sm sd-icon-btn"
                            onClick={() => copyText(sub.minecraftNick)}
                            aria-label="Copiar nick"
                          >
                            <FaCopy aria-hidden="true" />
                          </button>
                        )}
                      </dd>
                      <dt>Billing</dt>
                      <dd>{sub.billing || '—'}</dd>
                      <dt>Pagamento</dt>
                      <dd>{sub.paymentMethod || '—'}</dd>
                      <dt>Válido até</dt>
                      <dd>{formatWhen(sub.currentPeriodEnd)}</dd>
                      {sub.stripeCustomerId && (
                        <>
                          <dt>Stripe cust.</dt>
                          <dd>
                            <span className="sd-mono">{sub.stripeCustomerId}</span>
                            <button
                              type="button"
                              className="admin-btn admin-btn--ghost admin-btn--sm sd-icon-btn"
                              onClick={() => copyText(sub.stripeCustomerId)}
                            >
                              <FaCopy />
                            </button>
                          </dd>
                        </>
                      )}
                      {sub.stripeSubscriptionId && (
                        <>
                          <dt>Stripe sub.</dt>
                          <dd>
                            <span className="sd-mono">{sub.stripeSubscriptionId}</span>
                            <button
                              type="button"
                              className="admin-btn admin-btn--ghost admin-btn--sm sd-icon-btn"
                              onClick={() => copyText(sub.stripeSubscriptionId)}
                            >
                              <FaCopy />
                            </button>
                          </dd>
                        </>
                      )}
                      {sub.mercadoPagoPaymentId && (
                        <>
                          <dt>PIX / MP</dt>
                          <dd>
                            <span className="sd-mono">{sub.mercadoPagoPaymentId}</span>
                            <button
                              type="button"
                              className="admin-btn admin-btn--ghost admin-btn--sm sd-icon-btn"
                              onClick={() => copyText(sub.mercadoPagoPaymentId)}
                            >
                              <FaCopy />
                            </button>
                          </dd>
                        </>
                      )}
                    </dl>
                  ) : (
                    <p className="sd-muted">Sem assinatura VIP encontrada.</p>
                  )}
                </div>

                <div className="sd-card">
                  <h4>Conta vinculada</h4>
                  {profile ? (
                    <dl className="sd-kv">
                      <dt>Nick</dt>
                      <dd>{profile.minecraftNick || profile.nickname || '—'}</dd>
                      <dt>Online</dt>
                      <dd>{profile.online ? 'Sim' : 'Não'}</dd>
                      <dt>Visto</dt>
                      <dd>{formatWhen(profile.lastSeen)}</dd>
                    </dl>
                  ) : (
                    <p className="sd-muted">Sem perfil vinculado.</p>
                  )}
                </div>

                {successMsg && <p className="sd-success">{successMsg}</p>}

                <div className="sd-card">
                  <h4>
                    <FaBolt aria-hidden="true" /> Ações rápidas
                  </h4>
                  <div className="sd-actions">
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={busy}
                          onClick={() => handleVipAction('portal_hint')}
                        >
                          <FaExternalLinkAlt /> Orientar portal Stripe
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={busy}
                          onClick={() =>
                            insertDraft(
                              'Pode confirmar o nick Minecraft exatamente como está no jogo? Assim alinhamos o rank.'
                            )
                          }
                        >
                          Pedir nick
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={busy}
                          onClick={() =>
                            insertDraft(
                              sub?.mercadoPagoPaymentId
                                ? `Encontrei o pagamento PIX ${sub.mercadoPagoPaymentId}. Já estou verificando a liberação.`
                                : 'Pode enviar o ID do pagamento PIX (ou comprovante com horário/valor)?'
                            )
                          }
                        >
                          Pedir / citar PIX
                        </button>
                        {sub?.minecraftNick && (
                          <button
                            type="button"
                            className="admin-btn admin-btn--ghost admin-btn--sm"
                            disabled={busy}
                            onClick={() =>
                              insertDraft(
                                `Seu nick na assinatura está como ${sub.minecraftNick}. Entre no servidor com esse nick para o rank aplicar.`
                              )
                            }
                          >
                            Avisar nick da assinatura
                          </button>
                        )}
                      </>
                    )}
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      disabled={!chat.userId}
                      onClick={() => onOpenVip?.(chat.userId)}
                    >
                      <FaCrown /> Abrir em VIP
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      disabled={!chat.userId}
                      onClick={() => onOpenProfiles?.(chat.userId)}
                    >
                      <FaLink /> Abrir em Contas
                    </button>
                  </div>
                </div>

                {canVipEdit ? (
                  <div className="sd-card">
                    <h4>
                      <FaCrown aria-hidden="true" /> Operações VIP
                    </h4>
                    <div className="sd-actions">
                      {sub?.status === 'active' && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--sm"
                          disabled={busy}
                          onClick={() => handleVipAction('reapply')}
                        >
                          <FaRedo /> Reaplicar cargos
                        </button>
                      )}
                      {sub && sub.status !== 'active' && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--sm"
                          disabled={busy}
                          onClick={() =>
                            setConfirmVip({
                              action: 'reactivate',
                              title: 'Reativar VIP',
                              message: `Reativar VIP de ${
                                sub.minecraftNick || chat.globalName
                              } e sincronizar cargos?`,
                              confirmLabel: 'Reativar',
                              tone: 'primary',
                              extra: { planId: editPlan || sub.planId },
                            })
                          }
                        >
                          <FaPlay /> Reativar VIP
                        </button>
                      )}
                      {sub?.status === 'active' && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={busy}
                          onClick={() =>
                            setConfirmVip({
                              action: 'revoke',
                              title: 'Revogar VIP',
                              message: `Revogar VIP de ${
                                sub.minecraftNick || 'jogador'
                              }? Remove cargos Discord/LuckPerms.`,
                              confirmLabel: 'Revogar',
                              tone: 'danger',
                            })
                          }
                        >
                          <FaBan /> Revogar VIP
                        </button>
                      )}
                      {sub && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={busy}
                          onClick={() =>
                            setConfirmVip({
                              action: 'extend_period',
                              title: 'Estender período',
                              message:
                                'Estender a validade por mais um ciclo (mensal/anual atual) e reaplicar cargos?',
                              confirmLabel: 'Estender',
                              tone: 'primary',
                            })
                          }
                        >
                          <FaCalendarPlus /> Estender validade
                        </button>
                      )}
                    </div>

                    {sub && (
                      <div className="sd-inline-form">
                        <label>
                          Nick Minecraft
                          <div className="sd-inline-row">
                            <input
                              value={editNick}
                              onChange={(e) => setEditNick(e.target.value)}
                              maxLength={16}
                              placeholder="Nick"
                            />
                            <button
                              type="button"
                              className="admin-btn admin-btn--sm"
                              disabled={
                                busy ||
                                !editNick.trim() ||
                                editNick === sub.minecraftNick
                              }
                              onClick={() =>
                                setConfirmVip({
                                  action: 'update_nick',
                                  title: 'Alterar nick VIP',
                                  message: `Alterar nick de ${sub.minecraftNick} para ${editNick}?`,
                                  confirmLabel: 'Salvar nick',
                                  tone: 'primary',
                                  extra: { minecraftNick: editNick.trim() },
                                })
                              }
                            >
                              <FaUserEdit />
                            </button>
                          </div>
                        </label>
                        <label>
                          Plano
                          <div className="sd-inline-row">
                            <select className="sd-select"
                              value={editPlan}
                              onChange={(e) => setEditPlan(e.target.value)}
                            >
                              {PLAN_OPTIONS.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="admin-btn admin-btn--sm"
                              disabled={busy || editPlan === sub.planId}
                              onClick={() =>
                                setConfirmVip({
                                  action: 'change_plan',
                                  title: 'Alterar plano',
                                  message: `Mudar plano de ${sub.planId} para ${editPlan} e reaplicar cargos?`,
                                  confirmLabel: 'Alterar plano',
                                  tone: 'primary',
                                  extra: { planId: editPlan },
                                })
                              }
                            >
                              Aplicar
                            </button>
                          </div>
                        </label>
                      </div>
                    )}

                    {!sub && (
                      <div className="sd-inline-form">
                        <p className="sd-muted">Conceder VIP manual neste Discord:</p>
                        <label>
                          Nick
                          <input
                            value={grantNick}
                            onChange={(e) => setGrantNick(e.target.value)}
                            maxLength={16}
                            placeholder="Nick Minecraft"
                          />
                        </label>
                        <label>
                          Plano
                          <select className="sd-select"
                            value={grantPlan}
                            onChange={(e) => setGrantPlan(e.target.value)}
                          >
                            {PLAN_OPTIONS.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="admin-btn admin-btn--sm"
                          disabled={busy || grantNick.trim().length < 3}
                          onClick={() =>
                            setConfirmVip({
                              action: 'grant',
                              title: 'Conceder VIP',
                              message: `Conceder ${grantPlan} para ${grantNick} neste Discord?`,
                              confirmLabel: 'Conceder',
                              tone: 'primary',
                              extra: {
                                minecraftNick: grantNick.trim(),
                                planId: grantPlan,
                                billing: 'monthly',
                              },
                            })
                          }
                        >
                          <FaCrown /> Conceder VIP
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="sd-card sd-card--locked">
                    <h4>
                      <FaLock aria-hidden="true" /> Operações VIP
                    </h4>
                    <p className="sd-muted">
                      Sem permissão para conceder ou gerenciar VIP neste chat.
                    </p>
                  </div>
                )}

                {chat.status !== 'closed' && (
                  <div
                    className={`sd-card sd-transfer${
                      canTransfer ? '' : ' sd-card--locked'
                    }`}
                  >
                    <h4>
                      {canTransfer ? null : <FaLock aria-hidden="true" />}{' '}
                      Transferir
                    </h4>
                    {canTransfer ? (
                      <>
                        <select
                          className="sd-select"
                          value={transferId}
                          onChange={(e) => setTransferId(e.target.value)}
                          onFocus={() => onReloadMembers?.()}
                        >
                          <option value="">
                            {guildMembersLoading
                              ? 'Carregando membros…'
                              : 'Selecionar staff…'}
                          </option>
                          {memberOptions.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          disabled={!transferId || busy}
                          onClick={() => {
                            const m = memberOptions.find(
                              (x) => String(x.id) === String(transferId)
                            );
                            runAction({
                              action: 'transfer',
                              chatId: chat.id,
                              toUserId: transferId,
                              toUserName: m?.name || transferId,
                            });
                          }}
                        >
                          Transferir atendimento
                        </button>
                      </>
                    ) : (
                      <p className="sd-muted">
                        Sem permissão para transferir atendimentos.
                      </p>
                    )}
                  </div>
                )}

                <div className="sd-card">
                  <h4>
                    <FaHistory aria-hidden="true" /> Histórico deste Discord
                  </h4>
                  {history.length === 0 ? (
                    <p className="sd-muted">Nenhum histórico carregado.</p>
                  ) : (
                    <ul className="sd-history">
                      {history.map((h) => (
                        <li key={h.id}>
                          <button
                            type="button"
                            className={`sd-history__item${
                              h.current ? ' is-current' : ''
                            }`}
                            disabled={h.current}
                            onClick={() => openChat(h.id)}
                          >
                            <span>
                              {SUPPORT_TOPICS.find((t) => t.id === h.topic)?.label ||
                                h.topic}{' '}
                              · {h.status}
                            </span>
                            <span className="sd-muted">
                              {formatWhen(h.createdAt)}
                              {h.resolution ? ` — ${h.resolution}` : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={confirmDelete?.title || 'Excluir'}
        message={confirmDelete?.message || ''}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        tone="danger"
        busy={busy}
        onCancel={() => !busy && setConfirmDelete(null)}
        onConfirm={executeDelete}
      />

      <ConfirmDialog
        open={Boolean(confirmVip)}
        title={confirmVip?.title || 'Confirmar'}
        message={confirmVip?.message || ''}
        confirmLabel={confirmVip?.confirmLabel || 'Confirmar'}
        cancelLabel="Cancelar"
        tone={confirmVip?.tone === 'primary' ? 'primary' : 'danger'}
        busy={busy}
        onCancel={() => !busy && setConfirmVip(null)}
        onConfirm={() =>
          handleVipAction(confirmVip.action, confirmVip.extra || {})
        }
      />
    </section>
  );
};

export default SupportDeskPanel;
